import React, {memo} from 'react';

import {Trans} from '../../libs/shared/helpers/src/internationalization/react';
import {RendererOf} from '../../libs/shared/helpers/src/react/reactHelpers';
import {NumberFormat} from '../internationalization/numberFormat';

/*
 * Props.
 */

interface FileSizeFormatterProps {
  value: number;
}

/*
 * Component.
 */

export const FileSizeFormatter = memo<FileSizeFormatterProps>((props) => {
  // Validate the value.
  const {value} = props;
  if (!Number.isInteger(value) || value < 0) {
    throw new Error('The file size should be a positive integer.');
  }

  // Find the unit and transform the value for display.
  const unit = findUnit(value);
  const displayValue = value / unit.base;

  // Render with localization.
  return <>{unitRenderers[unit.name](displayValue)}</>;
});

/*
 * Unit logic.
 */

export enum UnitNamesEnum {
  BYTE,
  KILOBYTE,
  MEGABYTE,
  GIGABYTE,
  TERABYTE,
}

interface Unit {
  name: UnitNamesEnum;
  base: number;
}

const unitBase = 1000;
const units: ReadonlyArray<Unit> = [
  {
    name: UnitNamesEnum.TERABYTE,
    base: unitBase ** 4,
  },
  {
    name: UnitNamesEnum.GIGABYTE,
    base: unitBase ** 3,
  },
  {
    name: UnitNamesEnum.MEGABYTE,
    base: unitBase ** 2,
  },
  {
    name: UnitNamesEnum.KILOBYTE,
    base: unitBase,
  },
  {
    name: UnitNamesEnum.BYTE,
    base: 1,
  },
];

export function findUnit(size: number) {
  const unit = units.find((u) => size >= u.base - 1);
  if (!unit) {
    throw new Error('A matching unit should always be found.');
  }

  return unit;
}

/*
 * Localized renderers.
 */

const numberFormatOptions: Intl.NumberFormatOptions = {maximumFractionDigits: 1};
type UnitRenderers = {[T in UnitNamesEnum]: RendererOf<number>};

// prettier-ignore
const unitRenderers: UnitRenderers = {
  [UnitNamesEnum.BYTE]: value => (
    <Trans id="filesize-byte">
      <NumberFormat value={value} format={numberFormatOptions} />B
    </Trans>
  ),
  [UnitNamesEnum.KILOBYTE]: value => (
    <Trans id="filesize-kilobyte">
      <NumberFormat value={value} format={numberFormatOptions} />KB
    </Trans>
  ),
  [UnitNamesEnum.MEGABYTE]: value => (
    <Trans id="filesize-megabyte">
      <NumberFormat value={value} format={numberFormatOptions} />MB
    </Trans>
  ),
  [UnitNamesEnum.GIGABYTE]: value => (
    <Trans id="filesize-gigabyte">
      <NumberFormat value={value} format={numberFormatOptions} />GB
    </Trans>
  ),
  [UnitNamesEnum.TERABYTE]: value => (
    <Trans id="filesize-terabyte">
      <NumberFormat value={value} format={numberFormatOptions} />TB
    </Trans>
  )
};
